<template>
  <div>
  <!-- ================================================================
       Dialog hiển thị lịch sử phiếu thu học sinh
       Sử dụng: <student-invoice-history-dialog ref="historyDialog" @payment="handlePayment" />
       Mở:      this.$refs.historyDialog.open(row, targetType)
       ================================================================ -->
  <dialog-base
    :visible.sync="visible"
    title="Lịch sử phiếu thu học sinh"
    size="xl"
    :show-confirm="false"
    cancel-text="Đóng"
    cancel-type="danger"
    :use-panel="false"
    top="6vh"
    @cancel="handleClose"
  >
    <!-- Thông tin học sinh -->
    <div class="sk-panel mb-4" style="margin-bottom: 10px">
      <div class="sk-panel-head" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
        <div style="display: flex; align-items: center;">
          <div class="sk-panel-icon sk-panel-icon--warning" style="margin-right: 15px;">
            <i class="el-icon-user" />
          </div>
          <div>
            <p class="sk-panel-title">{{ studentName || 'Đang tải...' }}</p>
            <p class="sk-panel-subtitle">
              Mã học sinh: <strong>{{ studentCode || '—' }}</strong> &mdash; Lớp/Nhóm/Gói hiện tại
            </p>
          </div>
        </div>
        <div style="display: flex; justify-content: flex-end">
          <el-button 
            type="success" 
            size="small" 
            icon="el-icon-download" 
            :loading="isExporting"
            @click="handleExportExcel"
            :disabled="tableData.length === 0"
          >
            Xuất Excel
          </el-button>
        </div>
      </div>
    </div>

    <!-- Bảng danh sách lịch sử phiếu thu -->
    <el-table
      v-loading="isLoading"
      :data="tableData"
      border
      style="width: 100%"
      class="sk-table"
      :empty-text="'Không có lịch sử phiếu thu'"
    >
      <el-table-column type="index" label="STT" width="50" align="center" />
      
      <el-table-column label="Tháng-Năm" width="110" align="center">
        <template slot-scope="{ row }">
          {{ row.month }}/{{ row.year }}
        </template>
      </el-table-column>

      <el-table-column label="Đợt" prop="turn" width="70" align="center" />

      <el-table-column label="Mã phiếu thu" prop="invoiceCode" align="center" min-width="200">
        <template slot-scope="{ row }">
          <span>{{ row.invoiceCode || '—' }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Lớp/nhóm" prop="targetName" align="center" min-width="140">
        <template slot-scope="{ row }">
          <span>{{ row.targetName || '—' }}</span>
        </template>
      </el-table-column>
      
      <el-table-column label="Thiếu/Thừa phiếu thu trước" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span :class="row.beforeMoney < 0 ? 'tdi-money--negative' : 'tdi-money--positive'">
            {{ row.beforeMoney | formatCurrency }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Phải thu" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money tdi-money--bold">{{ row.sumMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Đã thu" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.collectedMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Tiền mặt" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.cashMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Chuyển khoản" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.transferMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Thiếu/Thừa còn lại" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span :class="row.afterMoney < 0 ? 'tdi-money--negative' : 'tdi-money--positive'">
            {{ row.afterMoney | formatCurrency }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Trạng thái" align="center" min-width="150">
        <template slot-scope="{ row }">
          <span v-if="row.status === 'FULLY_PAID'" style="color: #67C23A">Đóng đủ 100%</span>
          <span v-else-if="row.status === 'PARTIALLY_PAID'" style="color: #E6A23C">Chưa đóng đủ</span>
          <span v-else-if="row.status === 'UNPAID'" style="color: #F56C6C">Chưa đóng 100%</span>
          <span v-else-if="row.status === 'UNBILLED'">Chưa có phiếu thu</span>
          <span v-else-if="row.status === 'OVERPAID'">Đóng thừa</span>
          <span v-else-if="row.status === 'FULLY_PAID_WITH_SURPLUS'" style="color: #67C23A">Đóng đủ thừa</span>
          <span v-else-if="row.status === 'PAID_WITH_DEFICIT'" style="color: #E6A23C">Đóng đủ thiếu</span>
          <span v-else>{{ row.status }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Tác vụ" width="180" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-tooltip content="Chi tiết phiếu thu" placement="top" :enterable="false">
            <el-button
              size="mini"
              type="info"
              icon="el-icon-document"
              aria-label="Chi tiết phiếu thu"
              circle
              :disabled="!row.idx"
              @click="handleInvoiceDetail(row)"
            />
          </el-tooltip>
          <el-tooltip content="Xuất excel" placement="top" :enterable="false">
            <el-button
              v-if="checkPermission(['tuition_excel_export_v4'])"
              size="mini"
              type="success"
              icon="el-icon-download"
              circle
              @click="handleExportRowExcel(row)"
            />
          </el-tooltip>
          <el-tooltip content="Thanh toán" placement="top" :enterable="false">
            <el-button
              v-if="checkPermission(['tuition_payment_v4'])"
              size="mini"
              type="primary"
              icon="el-icon-wallet"
              circle
              @click="handlePayment(row)"
            />
          </el-tooltip>
          <el-tooltip content="Xoá phiếu thu lớp/nhóm cũ" placement="top" :enterable="false">
            <el-button
              v-if="checkPermission(['tuition_fees_services_action_v4']) && isDeletable(row)"
              size="mini"
              type="danger"
              icon="el-icon-delete"
              circle
              :loading="deletingIdx === row.idx"
              @click="handleDeleteOldInvoice(row)"
            />
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
  </dialog-base>
    <invoice-revenue-detail-dialog
      ref="invoiceRevenueDetailDialog"
      @saved="handleInvoiceDetailSaved"
    />
  </div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import InvoiceRevenueDetailDialog from "@/components/tutition_v3/dialog/InvoiceRevenueDetailDialog.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { exportStudentInvoiceHistoryExcel } from "@/commonFuncion/exportStudentInvoiceHistoryExcel";
import { exportSelectedStudentsExcel } from "@/commonFuncion/exportTuitionStudentListExcel";
import { mapState, mapGetters } from "vuex";
import checkPermission from "@/utils/permission";

export default {
  name: "StudentInvoiceHistoryDialog",
  components: { DialogBase, InvoiceRevenueDetailDialog },
  data() {
    return {
      visible: false,
      isLoading: false,
      isExporting: false,
      studentName: "",
      studentCode: "",
      tableData: [],
      // Target tracking state
      currentTargetType: "",
      currentTargetId: null,
      idStudent: null,
      deletingIdx: "",
    };
  },
  computed: {
    ...mapState({
      schoolData: (state) => state.schoolStore.schoolData,
    }),
    ...mapGetters("schoolStore", ["schoolInfo"]),
  },
  methods: {
    checkPermission,
    // ─── Public API ────────────────────────────────────────────────
    /**
     * Mở dialog và load danh sách lịch sử phiếu thu học sinh
     * @param {Object} row - dữ liệu dòng học sinh
     * @param {String} targetType - CLASS hoặc GROUP
     * @param {Number|String} currentTargetId - ID lớp/nhóm hiện tại của học sinh
     */
    open(row, targetType, currentTargetId) {
      this.studentName = row.fullName || "";
      this.studentCode = row.studentCode || "";
      this.currentTargetType = targetType;
      this.currentTargetId = currentTargetId || (targetType === "CLASS" ? row.idClass : row.idGroup);
      this.idStudent = row.idStudent;
      this.tableData = [];
      this.deletingIdx = "";
      this.visible = true;
      this.fetchData(row.idStudent, targetType);
    },

    // ─── API ───────────────────────────────────────────────────────
    async fetchData(idStudent, targetType) {
      this.isLoading = true;
      try {
        const res = await ManagingStudentInvoiceService.getStudentInvoiceHistory(idStudent, targetType);
        const data = res?.data?.data || [];
        this.tableData = data.map((item) => ({
          ...item,
          beforeMoney: Number(item.beforeMoney || 0),
          sumMoney: Number(item.sumMoney || 0),
          collectedMoney: Number(item.collectedMoney || 0),
          cashMoney: Number(item.cashMoney || 0),
          transferMoney: Number(item.transferMoney || 0),
          afterMoney: Number(item.afterMoney || 0),
        }));
      } catch (err) {
        console.error(err);
        NotifyMessage.leftCornerNotificationError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Không thể tải lịch sử phiếu thu học sinh!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    handleClose() {
      this.visible = false;
    },

    handlePayment(row) {
      this.visible = false;
      this.$emit("payment", row);
    },

    handleInvoiceDetail(row) {
      this.$refs.invoiceRevenueDetailDialog.open({
        ...row,
        fullName: row.fullName || this.studentName,
        studentCode: row.studentCode || this.studentCode,
      });
    },

    async handleInvoiceDetailSaved() {
      this.$emit("saved");
      if (this.visible) {
        await this.fetchData(this.idStudent, this.currentTargetType);
      }
    },

    isDeletable(row) {
      if (row.status !== 'UNPAID') return false;
      if (!this.currentTargetId) return false;
      const invoiceTargetId = this.currentTargetType === 'CLASS' ? (row.idClass || row.idTarget || row.targetId) : (row.idGroup || row.idTarget || row.targetId);
      return invoiceTargetId && Number(invoiceTargetId) !== Number(this.currentTargetId);
    },

    handleDeleteOldInvoice(row) {
      if (!row || !row.idx) return;

      this.$confirm(
        `Bạn có chắc chắn muốn xoá phiếu thu đợt ${row.turn} tháng ${row.month}/${row.year} của lớp/nhóm cũ không? Hành động này không thể hoàn tác.`,
        "Xác nhận xoá phiếu thu",
        {
          distinguishCancelAndClose: true,
          closeOnClickModal: false,
          confirmButtonText: "Đồng ý",
          cancelButtonText: "Hủy",
          type: "warning"
        }
      )
        .then(async () => {
          this.deletingIdx = row.idx;
          const payload = {
            idx: row.idx,
            currentTargetId: Number(this.currentTargetId),
            currentTargetType: this.currentTargetType,
          };

          try {
            const response = await ManagingStudentInvoiceService.deleteInvoiceByIds(payload);
            const responseData = response?.data;
            if (responseData && responseData.data === true) {
              NotifyMessage.leftCornerNotificationSuccess("Xoá phiếu thu cũ thành công.");
              this.$emit("saved");
              await this.fetchData(this.idStudent, this.currentTargetType);
            } else {
              const msg = responseData?.message || "Không thể xoá phiếu thu vì phiếu không tồn tại hoặc không còn đủ điều kiện xoá.";
              NotifyMessage.leftCornerNotificationError(msg);
            }
          } catch (error) {
            console.error(error);
            const errorMsg = error?.response?.data?.message || "Không thể thực hiện xoá phiếu thu lớp/nhóm cũ.";
            NotifyMessage.leftCornerNotificationError(errorMsg);
          } finally {
            this.deletingIdx = "";
          }
        })
        .catch(() => {
          // Cancelled
        });
    },

    async handleExportExcel() {
      if (this.tableData.length === 0) return;
      this.isExporting = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 500)); // Mock loading delay
        await exportStudentInvoiceHistoryExcel({
          historyData: this.tableData,
          studentName: this.studentName,
          studentCode: this.studentCode
        });
        NotifyMessage.leftCornerNotificationSuccess("Xuất excel thành công.");
      } catch (error) {
        console.error(error);
        NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra khi xuất excel.");
      } finally {
        this.isExporting = false;
      }
    },

    async handleExportRowExcel(row) {
      const revenueMap = new Map();
      if (Array.isArray(row.invoiceDetails)) {
        row.invoiceDetails.forEach(detail => {
          if (detail.revenueCode) {
            revenueMap.set(detail.revenueCode, detail.revenueName || '');
          }
        });
      }
      const feeColumns = [];
      let index = 0;
      for (const [revenueCode, revenueName] of revenueMap) {
        feeColumns.push({
          key: revenueCode,
          prop: `moneyKt${index + 1}`,
          label: revenueName || `Khoản thu ${index + 1}`,
        });
        index++;
      }

      const title = `CHI TIẾT PHIẾU THU - ${row.month}/${row.year} - ĐỢT ${row.turn}`;

      this.isExporting = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 500)); // Mock loading delay
        await exportSelectedStudentsExcel({
          invoices: [row],
          schoolInfo: this.schoolInfo,
          feeColumns: feeColumns,
          title: title
        });
        NotifyMessage.leftCornerNotificationSuccess("Xuất excel thành công.");
      } catch (error) {
        console.error(error);
        NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra khi xuất excel.");
      } finally {
        this.isExporting = false;
      }
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

/* ─── Money helper classes ─────────────────────────────────── */
.tdi-money {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-size: 13px;
}
.tdi-money--bold     { font-weight: 700; color: #1e293b; }
.tdi-money--negative { color: #dc2626; font-weight: 600; }
.tdi-money--positive { color: #16a34a; font-weight: 600; }
</style>
